'use client';

import styled, { css, keyframes } from 'styled-components';

import type { Shadow } from '.';

type TextBaseProps = {
  $scale: number;
  $fontFamily: string;
  $fontWeight: number;
  $fontSize: number;
  $lineHeight?: number;
  $top?: number;
  $bottom?: number;
  $left?: number;
  $right?: number;
  $width?: number;
  $textAlign?: 'left' | 'center' | 'right';
};

export type TextAnimationType = 'fadeIn' | 'fadeInLeft' | 'fadeInRight' | 'fadeInUp' | 'fadeInDown' | 'scale';

/**
 * 텍스트 Fade-In 애니메이션
 */
// 텍스트 페이드인: 선수 번호, 이름
const textFadeIn = keyframes`
  0%, 10% {
    opacity: 0;
  }
  20%, 100% {
    opacity: 1;
  }
`;

const textFadeInLeft = keyframes`
  0%, 10% {  // 처음 10%는 안보이는 상태 (딜레이)
    opacity: 0;
    transform: translateX(-4px);
  }
  20%, 100% {  // 20%~80% 구간에서는 보이게
    opacity: 1;
    transform: translateX(0);
  }
`;

// 새로운 애니메이션들 추가
const textFadeInRight = keyframes`
  0%, 10% {
    opacity: 0;
    transform: translateX(4px);
  }
  20%, 100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

const textFadeInUp = keyframes`
  0%, 10% {
    opacity: 0;
    transform: translateY(4px);
  }
  20%, 100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const textFadeInDown = keyframes`
  0%, 10% {
    opacity: 0;
    transform: translateY(-4px);
  }
  20%, 100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleFadeIn = keyframes`
  0%, 10% {
    opacity: 0;
    transform: scale(0.8);
  }
  20%, 100% {
    opacity: 1;
    transform: scale(1);
  }
`;

// 애니메이션 타입에 따른 스타일 매핑
const getAnimationByType = (type: TextAnimationType) => {
  switch (type) {
    case 'fadeIn':
      return textFadeIn;
    case 'fadeInLeft':
      return textFadeInLeft;
    case 'fadeInRight':
      return textFadeInRight;
    case 'fadeInUp':
      return textFadeInUp;
    case 'fadeInDown':
      return textFadeInDown;
    case 'scale':
      return scaleFadeIn;
    default:
      return textFadeInLeft;
  }
};

const Container = styled.div<{ $scale: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  text-align: center;

  user-select: none;
  will-change: transform;
`;

const CharWrapper = styled.span`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

// 그림자 위 기본 텍스트
const MainChar = styled.span<{ $color: string; $animation?: TextAnimationType }>`
  position: relative;
  display: inline-block;
  color: ${({ $color }) => $color};
  opacity: ${({ $animation }) => ($animation ? 0 : 1)};
  z-index: 2;
  animation: ${({ $animation }) =>
    $animation
      ? css`
          ${getAnimationByType($animation)} 10s ease-in forwards
        `
      : 'none'};
  min-width: 0.25em;
`;

// 그림자 텍스트 스타일 파싱 및 생성 함수
export const textShadowCss = (value?: Shadow | Shadow[] | string) => {
  let list: Shadow[] = [];
  if (Array.isArray(value)) list = value;
  else if (!value) list = [];
  else if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      list = Array.isArray(parsed) ? parsed : [];
    } catch {
      list = [];
    }
  } else {
    list = [value];
  }

  const shadow = list
    .map(
      ({ xOffset = 0, yOffset = 0, blur = 0, color = 'rgba(0,0,0,1)' }) =>
        `${xOffset}px ${yOffset}px ${blur}px ${color}`,
    )
    .join(', ');

  return css`
    text-shadow: ${list.length ? shadow : 'none'};
  `;
};

// 그림자 텍스트
const ShadowChar = styled.span<{
  $shadow?: Shadow[];
  $animation?: TextAnimationType;
}>`
  position: absolute;
  top: 0;
  left: 0;
  display: inline-block;
  color: transparent;
  ${({ $shadow }) => textShadowCss($shadow)};
  opacity: ${({ $animation }) => ($animation ? 0 : 1)};
  z-index: 1;
  animation: ${({ $animation }) =>
    $animation
      ? css`
          ${getAnimationByType($animation)} 10s ease-in forwards
        `
      : 'none'};
  min-width: 0.25em;
`;

// 위치 지정 함수
const place = ({ $top, $bottom, $left, $right, $scale }: TextBaseProps) => css`
  /* Y축: top 우선, 없으면 bottom, 둘 다 없으면 center */
  top: ${$top != null ? `${$scale * $top}px` : $bottom == null ? '50%' : 'auto'};
  bottom: ${$top != null ? 'auto' : $bottom != null ? `${$scale * $bottom}px` : 'auto'};

  /* X축: left 우선, 없으면 right, 둘 다 없으면 center */
  left: ${$left != null ? `${$scale * $left}px` : $right == null ? '50%' : 'auto'};
  right: ${$left != null ? 'auto' : $right != null ? `${$scale * $right}px` : 'auto'};

  /* 양쪽 다 비어야 해당 축만 중앙 translate */
  transform: translate(
    ${$left == null && $right == null ? '-50%' : '0'},
    ${$top == null && $bottom == null ? '-50%' : '0'}
  );
`;

const justifyMap = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
} as const;

// 전체 기본 텍스트 스타일
const TextBase = styled.p<TextBaseProps>`
  position: absolute;

  ${place}

  display: flex;
  justify-content: ${({ $textAlign }) => justifyMap[$textAlign ?? 'center']};
  align-items: center;
  text-align: ${({ $textAlign }) => $textAlign ?? 'center'};
  width: ${({ $width, $scale }) => ($width != null ? `${$scale * $width}px` : 'auto')};

  user-select: none;
  will-change: transform;

  span {
    font-family: inherit;
  }

  ${MainChar}, ${ShadowChar} {
    font-size: ${({ $scale, $fontSize }) => $scale * $fontSize}px;
    font-weight: ${({ $fontWeight }) => $fontWeight};
    font-family: ${({ $fontFamily }) => $fontFamily};
    ${({ $scale, $lineHeight }) => $lineHeight && `line-height: ${$scale * $lineHeight}px;`}
  }
`;

const TextLayerStyled = {
  Container,
  CharWrapper,
  MainChar,
  ShadowChar,
  TextBase,
};

export default TextLayerStyled;
